// @flow

import React from 'react';
import { connect } from 'react-redux';
import glamorous, { Div, Span } from 'glamorous';
import { get, getOr } from 'lodash/fp';
import styled from '@emotion/styled';

import t from 'app/utils/translate';
import Modal from 'app/components/commons/Modal';
import MatchUpLine from '../../components/challenge/MatchUpLine';
import PlayerAvatar from '../../components/challenge/PlayerAvatar';
import PlayerLabels from '../../components/challenge/PlayerLabels';
import AnimateNumber from '../../components/commons/AnimateNumber';
import Responsive from '../../components/responsive/Responsive';
import { Back } from '../../constants/cssEase';
import { formatName } from '../../utils/displayUtils';
import { fetchTeamGameDataThrottle, fetchPlayerGameDataThrottle } from '../../api/onesport';
import decodePlayerGameStats from '../../normalizers/decodePlayerGameStats';
import decodeTeamGameStats from '../../normalizers/decodeTeamGameStats';

const LivePlayer = ({
  awayAbbr,
  progress,
  homeAbbr,
  hideMatchUpLine = false,
  image,
  initials,
  isHome,
  isTeam,
  isLosing,
  isWinning,
  isYou,
  labels,
  name,
  reverseLayout,
  reverseOrder,
  score,
  entityId,
  gameId,
  sportId
}: Props) => {
  const showStatsIcon = true;
  const isCompletedView = true;

  const [showOnesportModal, setShowOnesportModal] = React.useState(false);
  const [statistics, setStatistics] = React.useState(undefined);
  const [liveGameError, setLiveGameError] = React.useState(undefined);

  const fetchLiveGame = () => {
    const decodeData = isTeam ? decodeTeamGameStats : decodePlayerGameStats;
    const fetchData = isTeam ? fetchTeamGameDataThrottle : fetchPlayerGameDataThrottle;

    if (!entityId) {
      setLiveGameError('Player or match not found');
      return;
    }

    fetchData(sportId, entityId, gameId)
      .then(res => {
        const decodedStatistics = decodeData(res.data);
        const playerStatistics = getOr([], 'statistics', decodedStatistics);

        setStatistics(playerStatistics);
      })
      .catch(() => {
        setLiveGameError('Error fetching game data :(');
      });
  };

  React.useEffect(() => {
    if (showOnesportModal) {
      fetchLiveGame(sportId, entityId, gameId);
    }
  }, [showOnesportModal]);

  const toggleOnesportModal = () => {
    setShowOnesportModal(!showOnesportModal);
  };

  const renderStatsTable = (isSmallScreen = false) => {
    if (!statistics || statistics.length <= 0) {
      return <ModalHeader>{liveGameError}</ModalHeader>;
    }

    return (
      <div>
        <CloseButton
          onClick={() => {
            toggleOnesportModal();
          }}
        />
        <ModalHeader>{isSmallScreen ? name : formatName(name, 14)}</ModalHeader>
        <Table>
          <thead>
            <TableHeadRow>
              <TableHeading initial></TableHeading>
              <TableHeading>{t('Qty')}</TableHeading>
              <TableHeading>{t('Pts')}</TableHeading>
            </TableHeadRow>
          </thead>
          <tbody>
            {statistics.map(stat => (
              <TableBodyRow key={stat.id}>
                <TableCell initial>{stat.name}</TableCell>
                <TableCell>{stat.quantity}</TableCell>
                <TableCell>{stat.score || 0}</TableCell>
              </TableBodyRow>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Responsive desktop>
      {isDesktop =>
        isDesktop ? (
          <Wrapper reverseLayout={reverseLayout} isLosing={isLosing}>
            <Stack
              alignItems={reverseLayout ? 'flex-end' : 'flex-start'}
              style={{ textTransform: 'uppercase' }}
            >
              {isYou ? 'YOU' : 'OPP'}
              <StyledAnimateNumber
                green={isYou && isWinning}
                red={isYou && isLosing}
                format="0,0.00"
                number={score}
              />
              {progress}
            </Stack>
            <Div
              display="flex"
              alignItems="center"
              flexDirection={reverseLayout ? 'row-reverse' : 'row'}
            >
              <Stack alignItems={reverseLayout ? 'flex-start' : 'flex-end'} padLeft padRight>
                <Name>{name}</Name>
                {!hideMatchUpLine && (
                  <MatchUpLine
                    awayTeam={awayAbbr}
                    homeTeam={homeAbbr}
                    highlightAway={!isHome}
                    highlightHome={isHome}
                    reverseOrder={reverseOrder}
                  />
                )}
                <PlayerLabels items={labels} />
              </Stack>
              <AvatarCell isWinning={isYou && isWinning} onClick={toggleOnesportModal}>
                <PlayerAvatar
                  size="large"
                  fallbackText={initials}
                  image={image}
                  showStatsIcon={showStatsIcon}
                  reverseLayout={reverseLayout}
                  isCompletedView={isCompletedView}
                />
              </AvatarCell>
            </Div>
            {showOnesportModal && (
              <Modal isOpen={showOnesportModal} showCloseButton={false} size="medium">
                {renderStatsTable()}
              </Modal>
            )}
          </Wrapper>
        ) : (
          <Wrapper reverseLayout={reverseLayout} isLosing={isLosing}>
            <Stack
              alignItems={reverseLayout ? 'flex-end' : 'flex-start'}
              style={{ textTransform: 'uppercase' }}
            >
              <AvatarCell isWinning={isYou && isWinning} onClick={toggleOnesportModal}>
                <PlayerAvatar
                  size="default"
                  fallbackText={initials}
                  image={image}
                  showStatsIcon={showStatsIcon}
                  reverseLayout={reverseLayout}
                  isCompletedView={isCompletedView}
                />
              </AvatarCell>
              <StyledAnimateNumber
                green={isYou && isWinning}
                red={isYou && isLosing}
                format="0,0.00"
                number={score}
              />
            </Stack>
            <MiddleSection reverseLayout={reverseLayout}>
              <Stack alignItems={reverseLayout ? 'flex-start' : 'flex-end'} padLeft padRight>
                <Name>{formatName(name, 14)}</Name>
                {!hideMatchUpLine && (
                  <MatchUpLine
                    awayTeam={awayAbbr}
                    homeTeam={homeAbbr}
                    highlightAway={!isHome}
                    highlightHome={isHome}
                    reverseOrder={reverseOrder}
                  />
                )}
                <Div display="flex" alignItems="center">
                  <Span paddingRight="0.2em">{progress}</Span>
                  <PlayerLabels items={labels} />
                </Div>
              </Stack>
            </MiddleSection>
            {showOnesportModal && (
              <Modal isOpen={showOnesportModal} showCloseButton={false} size="medium">
                {renderStatsTable(true)}
              </Modal>
            )}
          </Wrapper>
        )
      }
    </Responsive>
  );
};

const connector = connect((state, { id, isTeam }) => {
  let entity = {};

  const item = isTeam ? state.challenge.teams[id] : state.challenge.players[id];
  entity = { ...item, onesportSportId: get('challenge.onesportSportId', state) };

  return {
    entityId: get('onesportEntityId', entity),
    gameId: get('onesportGameId', entity),
    sportId: get('onesportSportId', entity)
  };
});

export default connector(LivePlayer);

interface Props {
  awayAbbr: string;
  homeAbbr: string;
  hideMatchUpLine?: boolean;
  image: string;
  initials: string;
  isHome: boolean;
  isTeam: boolean;
  isLosing: boolean;
  isWinning: boolean;
  isYou: boolean;
  labels: Array<Object>;
  name: string;
  progress: string;
  reverseLayout: boolean;
  reverseOrder: boolean;
  score: number;
  entityId?: string;
  gameId?: string;
  sportId?: string;
}

const Wrapper = glamorous.div(
  {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'white',
    justifyContent: 'space-between'
  },
  ({ theme, reverseLayout, isLosing }) => ({
    // '& > *': {
    //   marginLeft: theme.spacing.small,
    // },
    transition: `all 0.4s ${Back.easeOut}`,
    transitionDelay: '0.5s',
    transform: isLosing ? 'translateZ(0) scale(0.9)' : 'translateZ(0) scale(1)',
    opacity: isLosing ? 0.75 : 1,
    padding: theme.spacing.xsmall,
    fontSize: theme.fontSize.xxsmall,
    flexDirection: reverseLayout ? 'row-reverse' : 'row',
    color: theme.color.basis2,
    fontWeight: theme.fontWeight.normal,
    [theme.mediaQuery.desktopAndUp]: {
      padding: theme.spacing.small,
      fontSize: theme.fontSize.small
    }
  })
);

const Name = glamorous.div({
  fontSize: '1.1em',
  textAlign: 'center'
});

const Stack = glamorous.div(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  ({ alignItems, theme }) => ({
    [theme.mediaQuery.desktopAndUp]: {
      alignItems
    }
  }),
  ({ padLeft, padRight }) => ({
    paddingLeft: padLeft ? '1em' : 0,
    paddingRight: padRight ? '1em' : 0
  })
);

const StyledAnimateNumber = glamorous(AnimateNumber)(
  ({ theme }) => ({
    transition: '0.3s all',
    transitionDelay: '0.7s',
    fontSize: '1.2em',
    fontWeight: theme.fontWeight.demiBold
  }),
  ({ theme, green, red }) => {
    if (green) {
      return {
        color: theme.color.green
      };
    } else if (red) {
      return {
        color: theme.color.danger
      };
    }
    return {
      color: theme.color.basis2
    };
  }
);

const AvatarCell = glamorous.div(({ theme, isWinning }) => ({
  transition: '0.3s all ease-out',
  transitionDelay: '0.6s',
  borderStyle: 'solid',
  borderWidth: isWinning ? 3 : 0,
  borderColor: theme.color.green,
  borderRadius: '100%'
}));

const MiddleSection = glamorous.div(({ reverseLayout, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  flexGrow: 1,
  [theme.mediaQuery.desktopAndUp]: {
    flexDirection: reverseLayout ? 'row-reverse' : 'row'
  }
}));

const ModalHeader = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  fontSize: '1.1em',
  fontWeight: 600,
  textAlign: 'center'
});

const Table = styled.table({
  width: '100%'
});

const TableHeadRow = styled.tr({});

const TableHeading = styled.th(
  {
    color: '#333',
    fontWeight: 'inherit',
    padding: '0.2em 0.75em'
  },
  ({ initial }) =>
    initial
      ? {
          textAlign: 'left'
        }
      : {
          textAlign: 'center',
          width: 80
        }
);

const TableBodyRow = styled.tr(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f2f2f2',
    borderColor: theme.color.white
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.color.white,
    borderColor: theme.color.basis6
  }
}));

const TableCell = styled.td(
  {
    padding: '0.5em 0.75em'
  },
  ({ initial }) =>
    initial
      ? {
          textAlign: 'left'
        }
      : {
          textAlign: 'center'
        }
);

const CloseButton = glamorous('button', {
  forwardProps: ['onClick'],
  withProps: {
    type: 'button'
  }
})(
  ({ theme }) => ({
    textAlign: 'right',
    backgroundColor: theme.color.white,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    color: theme.color.basis4,
    fontSize: theme.fontSize.large,
    height: 30,
    marginTop: 10,
    outline: 'none',
    textTransform: 'uppercase',
    width: '100%',
    outlineWidth: 0
  }),
  'ion-ios-close'
);
